import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import { BarChart } from '@mui/x-charts/BarChart';
import { PieChart } from '@mui/x-charts/PieChart';
import type React from 'react';
import type { PipelineData } from 'services/types/insuranceDashboard';
import { NAVY, RADIUS_COIN, SHADOW, SURFACE_CONTAINER_LOW, SURFACE_CONTAINER_LOWEST, YELLOW } from 'styles/tokens';

import PolicyFunnel from './PolicyFunnel';
import { PLAN_NAME_COLORS } from './policyStatusConstants';

const BLUE = '#2C5889';

interface Props {
  totalUF: number;
  policyCount: number;
  goalUF: number;
  goalProgress: number;
  pipeline?: PipelineData;
}

const cardSx = {
  borderRadius: RADIUS_COIN,
  boxShadow: SHADOW,
  border: 'none',
  bgcolor: SURFACE_CONTAINER_LOWEST,
} as const;

const labelSx = {
  color: 'text.secondary',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  fontSize: '0.75rem',
  fontWeight: 600,
} as const;

const chartCardSx = {
  ...cardSx,
  flex: 1,
  minWidth: 350,
  p: 3,
} as const;

const SellerView: React.FC<Props> = ({ totalUF, policyCount, goalUF, goalProgress, pipeline }) => {
  return (
    <Box>
      {/* KPI Cards — asymmetric hero layout */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr' },
          gap: 2.5,
          mb: 3,
        }}
      >
        {/* Hero card: Total UF */}
        <Card sx={{ ...cardSx, gridColumn: { md: 'span 2' }, position: 'relative', overflow: 'hidden' }}>
          <CardContent sx={{ p: { xs: 3, md: 4 }, '&:last-child': { pb: { xs: 3, md: 4 } } }}>
            {/* Decorative accent */}
            <Box
              sx={{
                position: 'absolute',
                top: -30,
                right: -30,
                width: 120,
                height: 120,
                borderRadius: '50%',
                bgcolor: `${YELLOW}15`,
                pointerEvents: 'none',
              }}
            />
            <Typography variant="subtitle2" gutterBottom sx={labelSx}>
              Total UF
            </Typography>
            <Typography
              sx={{
                fontWeight: 800,
                color: NAVY,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                lineHeight: 1.1,
                fontFamily: 'Jost, sans-serif',
                letterSpacing: '-0.02em',
              }}
            >
              {totalUF.toFixed(2)}
            </Typography>
          </CardContent>
        </Card>

        {/* Polizas vendidas */}
        <Card sx={cardSx}>
          <CardContent>
            <Typography variant="subtitle2" gutterBottom sx={labelSx}>
              Pólizas vendidas
            </Typography>
            <Typography sx={{ fontWeight: 700, color: NAVY, fontSize: '2rem', lineHeight: 1.2 }}>
              {policyCount}
            </Typography>
          </CardContent>
        </Card>

        {pipeline && (
          <>
            <Card sx={cardSx}>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom sx={labelSx}>
                  Cotizaciones
                </Typography>
                <Typography sx={{ fontWeight: 700, color: NAVY, fontSize: '2rem', lineHeight: 1.2 }}>
                  {pipeline.totalQuotes}
                </Typography>
              </CardContent>
            </Card>
            <Card sx={cardSx}>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom sx={labelSx}>
                  Tasa de conversión
                </Typography>
                <Typography sx={{ fontWeight: 700, color: NAVY, fontSize: '2rem', lineHeight: 1.2 }}>
                  {pipeline.conversionRate}%
                </Typography>
              </CardContent>
            </Card>
          </>
        )}
      </Box>

      {/* Goal progress */}
      <Card sx={{ ...cardSx, p: 3, mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom sx={labelSx}>
          Meta del mes
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, alignItems: 'baseline' }}>
          <Typography variant="body2" sx={{ color: NAVY }}>
            {totalUF.toFixed(2)} / {goalUF} UF
          </Typography>
          <Typography sx={{ fontWeight: 800, color: NAVY, fontSize: '1.5rem' }}>{goalProgress}%</Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={Math.min(goalProgress, 100)}
          sx={{
            height: 16,
            borderRadius: 8,
            backgroundColor: SURFACE_CONTAINER_LOW,
            '& .MuiLinearProgress-bar': {
              backgroundColor: YELLOW,
              borderRadius: 8,
              transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
            },
          }}
        />
      </Card>

      {/* Policy funnel */}
      {pipeline && Object.keys(pipeline.policiesByStatus).length > 0 && (
        <Card sx={{ ...chartCardSx, mb: 3, minWidth: 0 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: NAVY }}>
            Embudo de pólizas
          </Typography>
          <PolicyFunnel policiesByStatus={pipeline.policiesByStatus} />
        </Card>
      )}

      {/* Charts row */}
      {pipeline && (pipeline.policiesByPlanName?.length || pipeline.simulationsByBrand?.length) && (
        <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap' }}>
          {pipeline.policiesByPlanName && pipeline.policiesByPlanName.length > 0 && (
            <Card sx={chartCardSx}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: NAVY }}>
                Plan contratado
              </Typography>
              <PieChart
                series={[
                  {
                    data: pipeline.policiesByPlanName.map((d, idx) => ({
                      id: idx,
                      value: d.count,
                      label: d.planName,
                      color: PLAN_NAME_COLORS[idx % PLAN_NAME_COLORS.length],
                    })),
                    highlightScope: { fade: 'global', highlight: 'item' },
                    innerRadius: 50,
                    valueFormatter: (item) => `${item.value} pólizas`,
                  },
                ]}
                height={300}
                margin={{ bottom: 40 }}
                slotProps={{
                  legend: {
                    direction: 'horizontal',
                    position: { vertical: 'bottom', horizontal: 'center' },
                  },
                }}
              />
            </Card>
          )}
          {pipeline.simulationsByBrand && pipeline.simulationsByBrand.length > 0 && (
            <Card sx={chartCardSx}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: NAVY }}>
                Cotizaciones por marca
              </Typography>
              <BarChart
                layout="horizontal"
                yAxis={[
                  {
                    scaleType: 'band',
                    data: pipeline.simulationsByBrand.map((d) => d.brand),
                    disableLine: true,
                    disableTicks: true,
                  },
                ]}
                xAxis={[{ disableLine: true, disableTicks: true, tickLabelStyle: { display: 'none' } }]}
                series={[
                  {
                    data: pipeline.simulationsByBrand.map((d) => d.simulationCount),
                    color: BLUE,
                    valueFormatter: (value: number | null) => `${value ?? 0} cotizaciones`,
                  },
                ]}
                height={Math.max(250, pipeline.simulationsByBrand.length * 40)}
                margin={{ left: 120, right: 40, bottom: 10 }}
                grid={{ vertical: false }}
                hideLegend={true}
              />
            </Card>
          )}
        </Box>
      )}
    </Box>
  );
};

export default SellerView;
